import {ContentEditable} from '@lexical/react/LexicalContentEditable';
import {getSize, Integer} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {ComponentPropsWithRef} from 'react';

export type ContentEditableProps = {
  $isInvalid?: boolean;
  $isDisabled?: boolean;
  $minHeight?: Integer;
  $isFramed?: boolean;
};

type StyledContentEditableProps = ContentEditableProps &
  ComponentPropsWithRef<typeof ContentEditable>;

const contentEditableVariants = cva(
  'block w-full border border-solid border-[var(--color-palettes-neutral-70-100)] bg-[var(--color-palettes-white-10-100)] px-2 py-[6px] text-[length:var(--text-size-small)] font-[var(--text-weight-default)] leading-[var(--text-leading-small)] tracking-[0] text-[var(--color-primary)] outline-none rounded-[var(--radius-small)] hover:border-[var(--color-severity-informational)] active:border-[var(--color-severity-informational)] active:[box-shadow:var(--shadow-active)] focus:border-[var(--color-severity-informational)] focus:[box-shadow:var(--shadow-active)] [&_.mentions]:bg-[var(--color-palettes-blue-30-100)] [&_.mentions]:text-[length:var(--text-size-small)] [&_.editor-link]:text-[var(--color-accent)] [&_.editor-link]:no-underline',
  {
    variants: {
      isInvalid: {
        true: 'border-[var(--color-severity-danger)]',
        false: '',
      },
      isDisabled: {
        true: 'cursor-not-allowed !border-[var(--color-palettes-neutral-70-40)] !bg-[var(--color-palettes-white-10-40)] text-[var(--color-primary)] ![box-shadow:none] hover:!border-[var(--color-palettes-neutral-70-40)] focus:!border-[var(--color-palettes-neutral-70-40)] [&_.editor-link]:cursor-not-allowed',
        false: '',
      },
      isFramed: {
        true: '!border-0 ![box-shadow:none] hover:!border-0 hover:![box-shadow:none] active:!border-0 active:![box-shadow:none] focus:!border-0 focus:![box-shadow:none]',
        false: '',
      },
    },
    defaultVariants: {
      isInvalid: false,
      isDisabled: false,
      isFramed: false,
    },
  }
);

/**
 * Tailwind equivalent of the legacy ContentEditable primitive. It accepts the same custom
 * props, forwards the same Lexical contenteditable element props, and renders the same div.
 */
export function StyledContentEditable(props: StyledContentEditableProps) {
  const contentEditableProps = {...props};
  delete contentEditableProps.$isInvalid;
  delete contentEditableProps.$isDisabled;
  delete contentEditableProps.$minHeight;
  delete contentEditableProps.$isFramed;
  const minHeight = props.$minHeight ? getSize(props.$minHeight) : 'auto';

  return (
    <ContentEditable
      {...contentEditableProps}
      className={cn(
        contentEditableVariants({
          isInvalid: props.$isInvalid,
          isDisabled: props.$isDisabled,
          isFramed: props.$isFramed,
        }),
        props.className
      )}
      style={{minHeight, ...props.style}}
    />
  );
}
